import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Account } from '@/features/accounts/types';
import { useDeleteAccount } from '@/features/accounts/removal/hooks/useDeleteAccount';
import { useAccountSchedules } from '@/features/accounts/schedule/hooks/useAccountSchedules';
import { useScheduleActivation } from '@/features/accounts/schedule/activation/hooks/useScheduleActivation';
import { useScheduleManagement } from '@/features/accounts/schedule/management/hooks/useScheduleManagement';
import { useScheduleControls } from '@/features/accounts/schedule/hooks/useScheduleControls';
import { useModalState } from '@/shared/hooks';
import { formatError } from '@/shared/api';
import AccountCardView from './AccountCard/AccountCardView';
import ScheduleActivationModals from '@/features/accounts/schedule/activation/components/ScheduleActivationModals';
import ScheduleManagementModals from '@/features/accounts/schedule/management/components/ScheduleManagementModals';
import RemoveAccountModal from '@/features/accounts/removal/components/RemoveAccountModal';
import { Button as EditPasswordButton } from '@/features/accounts/credentials/components/EditPassword';

interface Props {
  account: Account;
  onActivated?: () => void;
}

export default function AccountCard({ account, onActivated }: Props) {
  const [removeError, setRemoveError] = useState<string | null>(null);
  const { workable, rest, isLoading, error } = useAccountSchedules(account.id);
  const deleteAccount = useDeleteAccount();
  const { isOpen: isRemoveOpen, open: openRemove, close: closeRemove } = useModalState();
  const intl = useIntl();

  const activation = useScheduleActivation({ onActivated });
  const management = useScheduleManagement();
  const scheduleActions = useScheduleControls(activation, management);

  const handleCloseRemove = () => {
    if (deleteAccount.isPending) {
      return;
    }
    closeRemove();
  };

  const handleConfirmRemove = async () => {
    setRemoveError(null);
    try {
      await deleteAccount.mutateAsync(account.id);
      closeRemove();
    } catch (error_) {
      setRemoveError(formatError(error_, intl.formatMessage({ id: 'accounts.card.removeError' })));
    }
  };

  return (
    <>
      <AccountCardView
        account={account}
        scheduleState={{ workable, rest, isLoading, error: error?.message ?? null }}
        scheduleActions={scheduleActions}
        onRemove={() => { setRemoveError(null); openRemove(); }}
        isRemoving={deleteAccount.isPending}
        editPasswordAction={<EditPasswordButton account={account} />}
      />
      <ScheduleActivationModals countryCode={account.country_code} activation={activation} />
      <ScheduleManagementModals countryCode={account.country_code} management={management} />
      <RemoveAccountModal
        isOpen={isRemoveOpen}
        onClose={handleCloseRemove}
        onConfirm={handleConfirmRemove}
        isSubmitting={deleteAccount.isPending}
        error={removeError}
      />
    </>
  );
}
